import React from 'react';
import { Bone, SkeletonRegion, range } from '../Skeleton';
import { CARD } from './teacherUi';

// Placeholder shapes for teacher pages and blocks while their data loads.
// Each skeleton mirrors the layout it stands in for (primitives in
// components/Skeleton.jsx). AI work uses the sparkle Loader instead.

function SectionTitleBones() {
  return (
    <div className="space-y-2">
      <Bone className="h-6 w-44 rounded-lg" />
      <Bone className="h-1 w-10 rounded-full" />
    </div>
  );
}

/** Icon tile + two text lines, the row shape most teacher lists use. */
function IconRow({ tile = 'w-10 h-10', trailing = null }) {
  return (
    <div className="flex items-center gap-3">
      <Bone className={`${tile} rounded-lg shrink-0`} />
      <div className="flex-1 min-w-0 space-y-2">
        <Bone className="h-4 w-3/5" />
        <Bone className="h-3 w-2/5" />
      </div>
      {trailing}
    </div>
  );
}

/** Title + meta line, no icon: rows in the content lists. */
function TextRow({ trailing = null }) {
  return (
    <div className="flex items-center gap-4">
      <div className="flex-1 min-w-0 space-y-2">
        <Bone className="h-4 w-1/2" />
        <Bone className="h-3 w-1/3" />
      </div>
      {trailing}
    </div>
  );
}

// ─── Content Studio ─────────────────────────────────────────────────────────

/** The four count tiles at the top of Content Studio. */
export function CountTilesSkeleton() {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" aria-hidden="true">
      {range(4).map(i => (
        <div key={i} className={`${CARD} px-5 py-4 flex items-center gap-4`}>
          <Bone className="w-10 h-10 rounded-lg shrink-0" />
          <div className="space-y-2"><Bone className="h-3 w-16" /><Bone className="h-7 w-10" /></div>
        </div>
      ))}
    </div>
  );
}

/** Deskey Session cards: title, synced time, status pill, routing row, actions. */
export function SessionCardsSkeleton() {
  return (
    <SkeletonRegion label="Loading sessions…" testId="audio-inbox-loading"
      className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-5">
      {range(6).map(i => (
        <div key={i} className={`${CARD} p-6 flex flex-col`}>
          <div className="flex items-start gap-3">
            <div className="flex-1 space-y-2"><Bone className="h-5 w-3/4" /><Bone className="h-4 w-1/3" /></div>
            <Bone className="h-6 w-20 rounded-md" />
          </div>
          <div className="mt-4 rounded-xl border border-slate-100 p-3"><IconRow tile="w-8 h-8" /></div>
          <div className="mt-6 flex gap-3">
            <Bone className="h-10 flex-1 rounded-lg" />
            <Bone className="h-10 w-10 rounded-lg" />
          </div>
        </div>
      ))}
    </SkeletonRegion>
  );
}

/** Rows inside one card (subject content, published lessons). */
export function ContentRowsSkeleton({ label = 'Loading content…', testId = 'content-rows-loading', rows = 5 }) {
  return (
    <SkeletonRegion label={label} testId={testId} className={`${CARD} divide-y divide-slate-100`}>
      {range(rows).map(i => (
        <div key={i} className="px-5 h-16 flex items-center">
          <div className="flex-1"><TextRow trailing={<Bone className="h-6 w-24 rounded-md shrink-0" />} /></div>
        </div>
      ))}
    </SkeletonRegion>
  );
}

/** Chapter choices in a picker modal (route a session, publish to lesson). */
export function ChapterListSkeleton() {
  return (
    <SkeletonRegion label="Loading chapters…" testId="chapter-list-loading" className="space-y-1.5">
      {range(4).map(i => (
        <div key={i} className="rounded-lg border border-slate-200 px-3 py-2.5 flex items-center gap-3">
          <Bone className="w-4 h-4 rounded-full shrink-0" />
          <Bone className="h-4 flex-1 max-w-[60%]" />
        </div>
      ))}
    </SkeletonRegion>
  );
}

// ─── Full pages opened from the studio ──────────────────────────────────────

function BackAndTitle() {
  return (
    <div className="flex items-center gap-4">
      <Bone className="w-9 h-9 rounded-xl shrink-0" />
      <div className="flex-1 space-y-2"><Bone className="h-3 w-40" /><Bone className="h-7 w-72 max-w-full rounded-lg" /></div>
    </div>
  );
}

/** Session review: transcript card, board photos, then the generate button. */
export function SessionReviewSkeleton() {
  return (
    <SkeletonRegion label="Loading session…" testId="session-review-loading" className="p-8 max-w-5xl mx-auto space-y-6">
      <BackAndTitle />
      <div className={`${CARD} p-5 space-y-3`}>
        <IconRow tile="w-9 h-9" />
        {range(5).map(i => <Bone key={i} className={`h-3.5 ${i === 4 ? 'w-2/3' : 'w-full'}`} />)}
      </div>
      <div className={`${CARD} p-5 space-y-4`}>
        <IconRow tile="w-9 h-9" />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {range(4).map(i => <Bone key={i} className="aspect-[4/3] rounded-lg" />)}
        </div>
      </div>
      <div className="flex justify-end gap-2"><Bone className="h-11 w-36 rounded-xl" /><Bone className="h-11 w-44 rounded-xl" /></div>
    </SkeletonRegion>
  );
}

/** One subject: heading, create row, content list. */
export function SubjectPageSkeleton() {
  return (
    <SkeletonRegion label="Opening subject…" testId="subject-page-loading" className="px-8 py-8 max-w-5xl mx-auto space-y-8">
      <BackAndTitle />
      <div className="flex gap-3"><Bone className="h-10 flex-1 rounded-lg" /><Bone className="h-10 w-28 rounded-lg" /></div>
      <SectionTitleBones />
      <div className={`${CARD} divide-y divide-slate-100`}>
        {range(4).map(i => <div key={i} className="px-5 h-16 flex items-center"><div className="flex-1"><TextRow /></div></div>)}
      </div>
    </SkeletonRegion>
  );
}

/** Content editor: header bar, framework sections, AI chat panel. */
export function EditorSkeleton() {
  return (
    <SkeletonRegion label="Opening session…" testId="editor-loading" className="h-full flex">
      <div className="flex-1 min-w-0 px-8 py-6 space-y-5">
        <div className="flex items-center gap-3">
          <Bone className="w-9 h-9 rounded-lg" />
          <Bone className="h-7 w-80 max-w-full rounded-lg" />
          <div className="flex-1" />
          <Bone className="h-10 w-28 rounded-lg" />
          <Bone className="h-10 w-28 rounded-lg" />
        </div>
        <div className="flex gap-2">{range(4).map(i => <Bone key={i} className="h-7 w-24 rounded-md" />)}</div>
        {range(5).map(i => (
          <div key={i} className={`${CARD} p-5`}>
            <IconRow tile="w-8 h-8" trailing={<Bone className="h-8 w-16 rounded-lg shrink-0" />} />
            {i === 0 && (
              <div className="mt-4 space-y-2.5">
                {range(4).map(j => <Bone key={j} className={`h-3.5 ${j === 3 ? 'w-1/2' : 'w-full'}`} />)}
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="hidden xl:flex flex-col w-[380px] shrink-0 border-l border-slate-200 bg-white p-4 gap-3">
        <Bone className="h-6 w-32" />
        <Bone className="h-16 w-4/5 rounded-xl" />
        <Bone className="h-10 w-3/5 rounded-xl self-end" />
        <div className="flex-1" />
        <Bone className="h-12 rounded-xl" />
      </div>
    </SkeletonRegion>
  );
}

/** Student-view lesson preview (inside the class drawer). */
export function LessonPreviewSkeleton() {
  return (
    <SkeletonRegion label="Loading lesson…" testId="session-preview-loading" className="max-w-3xl mx-auto px-6 py-10 space-y-4">
      <Bone className="h-9 w-3/4 rounded-lg" />
      <Bone className="h-4 w-1/3" />
      <Bone className="h-48 rounded-2xl mt-6" />
      {range(6).map(i => <Bone key={i} className={`h-4 ${i % 3 === 2 ? 'w-4/5' : 'w-full'}`} />)}
    </SkeletonRegion>
  );
}

// ─── Substitution ───────────────────────────────────────────────────────────

/** Substitution cards: subject, class and dates, status pill, action buttons. */
export function SubstitutionCardsSkeleton() {
  return (
    <SkeletonRegion label="Loading substitutions…" testId="substitutions-loading" className="space-y-4">
      {range(3).map(i => (
        <div key={i} className={`${CARD} p-5 space-y-4`}>
          <div className="flex items-start gap-3">
            <div className="flex-1"><TextRow /></div>
            <Bone className="h-6 w-20 rounded-md" />
          </div>
          <div className="flex gap-2"><Bone className="h-10 w-28 rounded-lg" /><Bone className="h-10 w-44 rounded-lg" /></div>
        </div>
      ))}
    </SkeletonRegion>
  );
}

// ─── My Classes ─────────────────────────────────────────────────────────────

/** Department → batch → class card grid. */
export function ClassGridSkeleton() {
  return (
    <SkeletonRegion label="Loading your classes…" testId="teacher-classes-loading" className="space-y-10">
      {range(2).map(g => (
        <div key={g}>
          <div className="mb-5"><SectionTitleBones /></div>
          <Bone className="h-4 w-28 mb-3" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {range(3).map(i => (
              <div key={i} className={`${CARD} p-5 space-y-4`}>
                <IconRow />
                <div className="flex gap-2"><Bone className="h-6 w-20 rounded-md" /><Bone className="h-6 w-16 rounded-md" /></div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </SkeletonRegion>
  );
}

/** Class name and meta line in the class drawer header. */
export function ClassHeaderBones() {
  return (
    <div className="flex-1 min-w-0 flex items-center gap-4 py-1" aria-hidden="true">
      <Bone className="w-12 h-12 rounded-xl shrink-0" />
      <div className="flex-1 space-y-2"><Bone className="h-6 w-56 max-w-full rounded-lg" /><Bone className="h-4 w-72 max-w-full" /></div>
    </div>
  );
}

function TopicRowBones() {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <IconRow tile="w-9 h-9" trailing={<div className="flex gap-1.5 shrink-0"><Bone className="h-8 w-16 rounded-lg" /><Bone className="h-8 w-8 rounded-lg" /></div>} />
    </div>
  );
}

/** Class drawer body: chapter rail on the left, topic rows on the right. */
export function ClassDrawerSkeleton() {
  return (
    <SkeletonRegion label="Loading class…" testId="class-drawer-loading" className="h-full flex">
      <div className="w-[300px] border-r border-slate-100 bg-white p-4 space-y-2 shrink-0">
        <Bone className="h-9 rounded-lg mb-3" />
        {range(6).map(i => <Bone key={i} className="h-12 rounded-lg" />)}
      </div>
      <div className="flex-1 min-w-0 p-6 space-y-3">
        <Bone className="h-6 w-48 rounded-lg" />
        <Bone className="h-8 w-full max-w-md rounded-md mb-2" />
        {range(4).map(i => <TopicRowBones key={i} />)}
      </div>
    </SkeletonRegion>
  );
}

/** Topic rows for one chapter. */
export function TopicRowsSkeleton() {
  return (
    <SkeletonRegion label="Loading topics…" testId="topics-loading" className="space-y-2">
      {range(3).map(i => <TopicRowBones key={i} />)}
    </SkeletonRegion>
  );
}

/** Class insights: summary card with three figures, then two list cards. */
export function ClassInsightsSkeleton() {
  return (
    <SkeletonRegion label="Loading class insights…" testId="ci-loading" className="space-y-4">
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <IconRow />
        <div className="grid grid-cols-3 gap-2.5 mt-4">
          {range(3).map(i => <div key={i} className="rounded-xl border border-slate-100 p-3 space-y-2"><Bone className="h-3 w-16" /><Bone className="h-7 w-12" /></div>)}
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {range(2).map(i => (
          <div key={i} className="rounded-2xl border border-slate-200 bg-white p-5 space-y-3">
            <Bone className="h-5 w-36" />
            {range(4).map(j => <div key={j} className="space-y-1.5"><Bone className="h-3.5 w-2/5" /><Bone className="h-2 w-full rounded-full" /></div>)}
          </div>
        ))}
      </div>
    </SkeletonRegion>
  );
}

// ─── DeskeyHUB ──────────────────────────────────────────────────────────────

/** Library cards on the DeskeyHUB browse tab. */
export function HubLibrariesSkeleton() {
  return (
    <SkeletonRegion label="Loading libraries…" testId="teacher-hub-loading"
      className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
      {range(6).map(i => <div key={i} className={`${CARD} p-5`}><IconRow /></div>)}
    </SkeletonRegion>
  );
}

/** Subject search + subject cards inside one library. */
export function HubSubjectsSkeleton() {
  return (
    <SkeletonRegion label="Loading subjects…" testId="teacher-hub-subjects-loading" className="space-y-3">
      <Bone className="h-9 w-full max-w-xs rounded-lg" />
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2.5">
        {range(6).map(i => <div key={i} className="bg-white border border-slate-200 rounded-xl p-3.5"><IconRow tile="w-9 h-9" /></div>)}
      </div>
    </SkeletonRegion>
  );
}

/** Document results in the DeskeyHUB picker. */
export function DocPickerSkeleton() {
  return (
    <SkeletonRegion label="Searching…" testId="teacher-hub-picker-loading" className="space-y-2">
      {range(4).map(i => (
        <div key={i} className="rounded-2xl p-3 border-2 border-slate-100"><IconRow tile="w-9 h-9" /></div>
      ))}
    </SkeletonRegion>
  );
}

/** Board photos: thumbnail strip, then the selected photo and its OCR text. */
export function BoardCapturesSkeleton() {
  return (
    <SkeletonRegion label="Loading board photos…" testId="bcp-loading" className="space-y-4">
      <div className="flex gap-2 overflow-hidden">
        {range(5).map(i => <Bone key={i} className="w-24 h-24 rounded-lg shrink-0" />)}
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <Bone className="aspect-[4/3] rounded-xl" />
        <div className="space-y-2.5">{range(6).map(i => <Bone key={i} className={`h-3.5 ${i === 5 ? 'w-1/2' : 'w-full'}`} />)}</div>
      </div>
    </SkeletonRegion>
  );
}
